'use client'

import { useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import Footer from '@/components/Footer'

type NodeId = 'reality' | 'time' | 'complexity' | 'tradeoffs' | 'evolution' | 'architecture' | null

const nodeData: Record<string, { label: string; explanation: string; x: number; y: number }> = {
  reality: {
    label: 'Reality',
    explanation: 'What exists today, who depends on it, and what breaks first.',
    x: 20,
    y: 40
  },
  time: {
    label: 'Time',
    explanation: 'Most failures happen after launch — when shortcuts compound quietly.',
    x: 80,
    y: 40
  },
  complexity: {
    label: 'Complexity',
    explanation: 'If a system can\'t explain itself, it doesn\'t deserve to exist.',
    x: 20,
    y: 80
  },
  tradeoffs: {
    label: 'Trade-offs',
    explanation: 'Every decision optimizes for something. The job is choosing consciously.',
    x: 80,
    y: 80
  },
  evolution: {
    label: 'Evolution',
    explanation: 'Systems change. Architecture that can\'t adapt will be replaced.',
    x: 50,
    y: 95
  },
  architecture: {
    label: 'Architecture',
    explanation: 'The structure that makes behavior visible, decisions traceable, and evolution possible.',
    x: 50,
    y: 60
  }
}

export default function About() {
  const [hoveredNode, setHoveredNode] = useState<NodeId>(null)
  const [selectedNode, setSelectedNode] = useState<NodeId>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null)

  const handleNodeHover = (nodeId: NodeId, event: React.MouseEvent) => {
    if (nodeId) {
      const rect = event.currentTarget.getBoundingClientRect()
      const container = (event.currentTarget as Element).closest('svg')?.parentElement
      if (container) {
        const containerRect = container.getBoundingClientRect()
        setTooltipPosition({
          x: rect.left - containerRect.left + rect.width / 2,
          y: rect.top - containerRect.top - 10
        })
      }
      setHoveredNode(nodeId)
    } else {
      setHoveredNode(null)
      if (!selectedNode) {
        setTooltipPosition(null)
      }
    }
  }

  const handleNodeClick = (nodeId: NodeId, event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault()
    event.stopPropagation()
    
    if (selectedNode === nodeId) {
      setSelectedNode(null)
      setTooltipPosition(null)
    } else {
      const rect = (event.currentTarget as Element).getBoundingClientRect()
      const container = (event.currentTarget as Element).closest('svg')?.parentElement
      if (container) {
        const containerRect = container.getBoundingClientRect()
        setTooltipPosition({
          x: rect.left - containerRect.left + rect.width / 2,
          y: rect.top - containerRect.top - 10
        })
      }
      setSelectedNode(nodeId)
    }
  }

  const activeNode = selectedNode || hoveredNode

  return (
    <div className="min-h-screen bg-bg-primary relative">
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          
          {/* Page Header */}
          <section className="pt-16 sm:pt-24 pb-12">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-light text-text-primary leading-tight mb-6">
              About
            </h1>
            <p className="text-2xl sm:text-3xl font-light text-text-primary leading-tight">
              I design systems that are meant to last.
            </p>
          </section>

          {/* Divider */}
          <div className="relative py-8">
            <div className="absolute top-0 left-0 right-0 h-px bg-border-subtle opacity-60"></div>
          </div>

          {/* Intro Essay */}
          <section className="pb-10 sm:pb-14">
            <h2 className="text-2xl sm:text-3xl font-light text-text-primary mb-8">
              Why longevity matters
            </h2>
            
            <div className="space-y-5 text-text-secondary leading-relaxed max-w-3xl">
              <p className="text-base">
                Most systems fail quietly. They don't crash dramatically — they accumulate technical debt, hide problems behind layers of abstraction, and become harder to reason about over time.
              </p>
              <p className="text-base">
                The decisions we make today compound, for better or worse.
              </p>
              <p className="text-base">
                That work often takes the form of production-ready web platforms and internal tools — from idea to deployment.
              </p>
              <p className="text-base">
                I care about longevity over speed because systems that age well reduce decision fatigue, make behavior visible, and create space for steady evolution. Clarity is a long-term advantage, not a nice-to-have.
              </p>
              <p className="text-base">
                When pressure builds, when scale changes, when time passes — that's when architecture shows itself. The question isn't whether systems will change. It's whether they can change without breaking.
              </p>
            </div>

            <div className="pt-10 pb-2">
              <p className="text-base font-mono text-text-primary">
                Judgment {'>'} tools.
              </p>
            </div>
          </section>

          {/* Divider */}
          <div className="relative py-8">
            <div className="absolute top-0 left-0 right-0 h-px bg-border-subtle opacity-60"></div>
          </div>

          {/* Photo + Presence Block */}
          <section className="pb-12 sm:pb-16">
            <h2 className="text-2xl sm:text-3xl font-light text-text-primary mb-12">
              Who I am
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 items-start mb-8">
              {/* Left: Photo */}
              <div className="relative md:col-span-1">
                <div className="aspect-[3/4] max-w-[320px] bg-bg-elevated border border-border-subtle rounded-sm overflow-hidden">
                  <img
                    src="/sharan.jpeg"
                    alt="Sharan"
                    className="w-full h-full object-cover"
                    style={{ aspectRatio: '3 / 4' }}
                  />
                </div>
              </div>
              
              {/* Right: Presence */}
              <div className="md:col-span-2 space-y-5 text-text-secondary leading-relaxed pt-2">
                <p className="text-base">
                  My work sits at the intersection of architecture, product thinking, and long-term decision making.
                </p>
                <p className="text-base">
                  I design for behavior over trends. Systems that make their constraints visible, their trade-offs explicit, and their evolution traceable.
                </p>
                <p className="text-base">
                  Every system is an architecture problem. The question is whether we're willing to see it that way.
                </p>
                <p className="text-base pt-2">
                  I work directly on a small number of systems at a time.
                </p>
              </div>
            </div>
          </section>

          {/* Divider */}
          <div className="relative py-8">
            <div className="absolute top-0 left-0 right-0 h-px bg-border-subtle opacity-60"></div>
          </div>

          {/* How I Think - Diagram + Principles as One Unit */}
          <section className="pb-10 sm:pb-14">
            <div className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-light text-text-primary mb-3">
                How I think
              </h2>
              <p className="text-sm text-text-muted/80 max-w-2xl leading-relaxed">
                Not a process. Not a framework. A way of seeing systems.
              </p>
            </div>

            {/* Combined Diagram + Principles Container */}
            <div className="bg-bg-elevated border border-border-subtle rounded-sm p-8 sm:p-12">
              {/* Diagram */}
              <div className="relative mb-8">
                <svg
                  viewBox="0 0 100 100"
                  className="w-full h-full mx-auto"
                  style={{ minHeight: '400px', maxHeight: '500px', maxWidth: '600px' }}
                >
                  {/* Connection lines */}
                  <g stroke="var(--border-subtle)" strokeWidth="0.3" opacity="0.4">
                    <line x1="20" y1="40" x2="50" y2="60" />
                    <line x1="80" y1="40" x2="50" y2="60" />
                    <line x1="20" y1="80" x2="50" y2="60" />
                    <line x1="80" y1="80" x2="50" y2="60" />
                    <line x1="50" y1="60" x2="50" y2="95" />
                  </g>

                  {/* Nodes */}
                  {Object.entries(nodeData).map(([id, data]) => {
                    const isActive = activeNode === id
                    return (
                      <g key={id}>
                        <circle
                          cx={data.x}
                          cy={data.y}
                          r={isActive ? 3.5 : 2.5}
                          fill={isActive ? 'var(--accent-primary)' : 'var(--text-muted)'}
                          opacity={isActive ? 1 : 0.6}
                          className="cursor-pointer transition-all duration-200"
                          onMouseEnter={(e) => handleNodeHover(id as NodeId, e)}
                          onMouseLeave={() => {
                            if (!selectedNode) {
                              setHoveredNode(null)
                              setTooltipPosition(null)
                            }
                          }}
                          onClick={(e) => handleNodeClick(id as NodeId, e)}
                          onTouchStart={(e) => handleNodeClick(id as NodeId, e)}
                          style={{ filter: isActive ? 'drop-shadow(0 0 4px var(--accent-primary))' : 'none' }}
                        />
                        <text
                          x={data.x}
                          y={data.y - 4}
                          textAnchor="middle"
                          fill={isActive ? 'var(--accent-primary)' : 'var(--text-muted)'}
                          fontSize="2.5"
                          fontFamily="var(--font-mono)"
                          opacity={isActive ? 1 : 0.7}
                          className="pointer-events-none select-none"
                          style={{ fontWeight: isActive ? '500' : '400' }}
                        >
                          {data.label}
                        </text>
                      </g>
                    )
                  })}
                </svg>

                {/* Tooltip */}
                {activeNode && tooltipPosition && (
                  <div
                    className="absolute bg-bg-secondary border border-border-subtle rounded-sm p-3 shadow-lg z-10 pointer-events-none"
                    style={{
                      left: `${tooltipPosition.x}px`,
                      top: `${tooltipPosition.y}px`,
                      transform: 'translate(-50%, -100%)',
                      maxWidth: '240px'
                    }}
                  >
                    <p className="text-xs font-mono text-accent-primary mb-1">
                      {nodeData[activeNode].label}
                    </p>
                    <p className="text-xs text-text-secondary leading-relaxed">
                      {nodeData[activeNode].explanation}
                    </p>
                  </div>
                )}
              </div>

              {/* Principles List - As Annotation/Legend */}
              <div className="space-y-5 pt-4 border-t border-border-subtle">
                <p className="text-xs text-text-muted font-mono uppercase tracking-wider mb-4">
                  How this translates to decisions
                </p>
                
                <div 
                  className={`space-y-2 transition-all duration-200 ${
                    activeNode === 'reality' 
                      ? 'bg-accent-subtle/30 rounded-sm p-3 -m-3 border-l-2 border-accent-primary' 
                      : ''
                  }`}
                >
                  <h3 className={`text-base font-light font-mono transition-colors duration-200 ${
                    activeNode === 'reality' ? 'text-accent-primary' : 'text-text-primary'
                  }`}>
                    Start with reality
                  </h3>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    What exists today, who depends on it, and what breaks first.
                  </p>
                </div>

                <div 
                  className={`space-y-2 transition-all duration-200 ${
                    activeNode === 'time' 
                      ? 'bg-accent-subtle/30 rounded-sm p-3 -m-3 border-l-2 border-accent-primary' 
                      : ''
                  }`}
                >
                  <h3 className={`text-base font-light font-mono transition-colors duration-200 ${
                    activeNode === 'time' ? 'text-accent-primary' : 'text-text-primary'
                  }`}>
                    Design for time
                  </h3>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    Most systems don't fail at launch — they fail after quiet compromises.
                  </p>
                </div>

                <div 
                  className={`space-y-2 transition-all duration-200 ${
                    activeNode === 'complexity' 
                      ? 'bg-accent-subtle/30 rounded-sm p-3 -m-3 border-l-2 border-accent-primary' 
                      : ''
                  }`}
                >
                  <h3 className={`text-base font-light font-mono transition-colors duration-200 ${
                    activeNode === 'complexity' ? 'text-accent-primary' : 'text-text-primary'
                  }`}>
                    Assume complexity is guilty
                  </h3>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    If a system can't explain itself, it doesn't deserve to exist.
                  </p>
                </div>

                <div 
                  className={`space-y-2 transition-all duration-200 ${
                    activeNode === 'tradeoffs' 
                      ? 'bg-accent-subtle/30 rounded-sm p-3 -m-3 border-l-2 border-accent-primary' 
                      : ''
                  }`}
                >
                  <h3 className={`text-base font-light font-mono transition-colors duration-200 ${
                    activeNode === 'tradeoffs' ? 'text-accent-primary' : 'text-text-primary'
                  }`}>
                    Make trade-offs visible
                  </h3>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    Every decision optimizes for something. The job is choosing consciously, not avoiding choices.
                  </p>
                </div>

                <div 
                  className={`space-y-2 transition-all duration-200 ${
                    activeNode === 'evolution' 
                      ? 'bg-accent-subtle/30 rounded-sm p-3 -m-3 border-l-2 border-accent-primary' 
                      : ''
                  }`}
                >
                  <h3 className={`text-base font-light font-mono transition-colors duration-200 ${
                    activeNode === 'evolution' ? 'text-accent-primary' : 'text-text-primary'
                  }`}>
                    Build for evolution
                  </h3>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    Systems change. Architecture that can't adapt is architecture that will be replaced.
                  </p>
                </div>

                <div 
                  className={`space-y-2 transition-all duration-200 ${
                    activeNode === 'architecture' 
                      ? 'bg-accent-subtle/30 rounded-sm p-3 -m-3 border-l-2 border-accent-primary' 
                      : ''
                  }`}
                >
                  <h3 className={`text-base font-light font-mono transition-colors duration-200 ${
                    activeNode === 'architecture' ? 'text-accent-primary' : 'text-text-primary'
                  }`}>
                    Architecture
                  </h3>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    The structure that makes behavior visible, decisions traceable, and evolution possible.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Divider - Transition to Philosophy */}
          <div className="relative py-6">
            <div className="absolute top-0 left-0 right-0 h-px bg-border-subtle opacity-60"></div>
          </div>

          {/* Philosophy Section */}
          <section className="pb-10 sm:pb-14">
            <h2 className="text-2xl sm:text-3xl font-light text-text-primary mb-10">
              Philosophy
            </h2>

            <div className="mb-12">
              <p className="text-3xl sm:text-4xl font-light text-accent-primary leading-relaxed italic mb-12">
                Calm is not a lack of ambition. It's a strategy.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-16">
              {/* Left Column - Observations */}
              <div className="space-y-6 text-text-secondary leading-relaxed max-w-sm">
                <p className="text-base">
                  Fast systems break quietly.
                </p>
                <p className="text-base">
                  Loud systems hide problems.
                </p>
                <p className="text-base">
                  Complexity must be earned.
                </p>
              </div>

              {/* Right Column - Intent */}
              <div className="space-y-6 text-text-secondary leading-relaxed max-w-sm">
                <p className="text-base">
                  I design for clarity, traceability, and steady evolution.
                </p>
                <p className="text-base">
                  Systems should explain themselves.
                </p>
                <p className="text-base">
                  Good architecture reduces decision fatigue over time.
                </p>
              </div>
            </div>

            {/* "My Job" Line - Made a Moment */}
            <div className="pt-16 pb-8 text-center">
              <p className="text-2xl sm:text-3xl text-text-secondary italic leading-relaxed font-light">
                My job is to design systems that age well.
              </p>
            </div>
          </section>

          {/* Divider */}
          <div className="relative py-8">
            <div className="absolute top-0 left-0 right-0 h-px bg-border-subtle opacity-60"></div>
          </div>

          {/* AI Section */}
          <section className="pb-10 sm:pb-12">
            <h2 className="text-xl sm:text-2xl font-light text-text-primary mb-8">
              On AI
            </h2>

            <div className="space-y-4 text-text-secondary leading-relaxed max-w-2xl">
              <p className="text-sm">
                We live in a time where AI is part of how thinking happens.
              </p>
              <p className="text-sm">
                I work with AI as augmentation, not automation. It's a tool for exploring possibilities, challenging assumptions, and accelerating decision-making — but judgment stays human.
              </p>
              <p className="text-sm">
                AI amplifies thinking. It doesn't replace it.
              </p>
            </div>
          </section>

          {/* Divider */}
          <div className="relative py-8">
            <div className="absolute top-0 left-0 right-0 h-px bg-border-subtle opacity-60"></div>
          </div>

          {/* Closing Section */}
          <section className="">
            <div className="max-w-2xl mx-auto">
              <div className="bg-bg-elevated border border-border-subtle rounded-sm p-10 sm:p-12 text-center space-y-5">
                <div className="space-y-4 text-text-secondary leading-relaxed">
                  <p className="text-base">
                    I work with a small number of systems at a time.
                  </p>
                  <p className="text-base">
                    When something here resonates, the next step is usually obvious.
                  </p>
                </div>
                <p className="text-base text-text-muted pt-4">
                  I don't optimize for volume. I optimize for systems that deserve care.
                </p>
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="relative pt-12">
            <Footer />
          </div>

        </div>
      </div>
    </div>
  )
}
